import React, { useState } from 'react';
import { Plus, Minus, DollarSign } from 'lucide-react';
import { COLORS } from '../../../../utils/constants';
import { adminAPI } from '../../../../services/api';
import { useAdminToast } from '../../../../hooks/useAdminToast';
import { ModalWrapper } from './ModalComponents';

const BalanceModal = ({ clientId, currentBalance, onClose, onSuccess }) => {
  const { toast } = useAdminToast();
  const [amount, setAmount] = useState('');
  const [operation, setOperation] = useState('add');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) return;
    setLoading(true);
    try {
      await adminAPI.adjustBalance(clientId, { amount: parseFloat(amount), operation, notes });
      onSuccess();
    } catch (err) {
      toast.apiError(err);
    } finally {
      setLoading(false);
    }
  };

  const previewBalance = () => {
    const amt = parseFloat(amount) || 0;
    if (operation === 'add') return currentBalance + amt;
    if (operation === 'subtract') return Math.max(0, currentBalance - amt);
    return amt;
  };

  return (
    <ModalWrapper title="Управление балансом" onClose={onClose}>
      {/* Current Balance */}
      <div style={{ padding: '16px', backgroundColor: COLORS.background, borderRadius: '10px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>Текущий баланс</div>
            <div style={{ color: COLORS.text, fontSize: '28px', fontWeight: '700' }}>${currentBalance.toFixed(2)}</div>
          </div>
          <DollarSign size={32} color={COLORS.primary} />
        </div>
      </div>

      {/* Operation Selection */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
        <button 
          onClick={() => setOperation('add')} 
          style={{
            flex: 1, 
            padding: '14px', 
            backgroundColor: operation === 'add' ? `${COLORS.success}20` : COLORS.background,
            border: `2px solid ${operation === 'add' ? COLORS.success : COLORS.border}`, 
            borderRadius: '10px',
            color: operation === 'add' ? COLORS.success : COLORS.textSecondary, 
            fontSize: '14px', 
            fontWeight: '600', 
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Plus size={18} />
            Начислить
          </div>
          <span style={{ fontSize: '10px', opacity: 0.7 }}>+$ на баланс</span>
        </button>
        <button 
          onClick={() => setOperation('subtract')} 
          style={{
            flex: 1, 
            padding: '14px', 
            backgroundColor: operation === 'subtract' ? `${COLORS.danger}20` : COLORS.background,
            border: `2px solid ${operation === 'subtract' ? COLORS.danger : COLORS.border}`, 
            borderRadius: '10px',
            color: operation === 'subtract' ? COLORS.danger : COLORS.textSecondary, 
            fontSize: '14px', 
            fontWeight: '600', 
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Minus size={18} />
            Списать
          </div>
          <span style={{ fontSize: '10px', opacity: 0.7 }}>-$ с баланса</span>
        </button>
      </div>

      {/* Amount Input */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Сумма ($)</label>
        <input 
          type="number" 
          value={amount} 
          onChange={(e) => setAmount(e.target.value)} 
          placeholder="0.00" 
          style={{ 
            width: '100%', 
            padding: '14px', 
            backgroundColor: COLORS.background, 
            border: `1px solid ${COLORS.border}`, 
            borderRadius: '10px', 
            color: COLORS.text, 
            fontSize: '20px',
            fontWeight: '600',
            boxSizing: 'border-box' 
          }} 
        />
      </div>

      {/* Preview */}
      {amount && parseFloat(amount) > 0 && (
        <div style={{ padding: '12px', backgroundColor: `${operation === 'add' ? COLORS.success : COLORS.danger}10`, borderRadius: '8px', marginBottom: '16px', border: `1px solid ${operation === 'add' ? COLORS.success : COLORS.danger}30` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Новый баланс:</span>
            <span style={{ color: operation === 'add' ? COLORS.success : COLORS.danger, fontSize: '18px', fontWeight: '700' }}>
              ${previewBalance().toFixed(2)}
            </span>
          </div>
          <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginTop: '4px' }}>
            {operation === 'add' ? '+' : '-'}${parseFloat(amount).toFixed(2)}
          </div>
        </div>
      )}

      {/* Notes */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Комментарий</label>
        <input 
          type="text" 
          value={notes} 
          onChange={(e) => setNotes(e.target.value)} 
          placeholder="Причина изменения..." 
          style={{ 
            width: '100%', 
            padding: '12px', 
            backgroundColor: COLORS.background, 
            border: `1px solid ${COLORS.border}`, 
            borderRadius: '8px', 
            color: COLORS.text, 
            fontSize: '14px', 
            boxSizing: 'border-box' 
          }} 
        />
      </div>

      {/* Submit Button */}
      <button 
        onClick={handleSubmit} 
        disabled={loading || !amount || parseFloat(amount) <= 0} 
        style={{ 
          width: '100%', 
          padding: '14px', 
          backgroundColor: loading || !amount ? COLORS.border : operation === 'add' ? COLORS.success : COLORS.danger, 
          border: 'none', 
          borderRadius: '10px', 
          color: '#fff', 
          fontSize: '15px', 
          fontWeight: '600', 
          cursor: loading || !amount ? 'not-allowed' : 'pointer' 
        }}
      >
        {loading ? 'Обработка...' : operation === 'add' ? `Начислить $${amount || 0}` : `Списать $${amount || 0}`}
      </button>
    </ModalWrapper>
  );
};

export default BalanceModal;
